import { useCallback, useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { assets } from '@/mocks/home';
import OpenRotorMark, { OPEN_ROTOR_RIM } from '@/components/base/OpenRotorMark';
import GateValve3D, { slotRect, type GateValveStatus, type SceneFrames } from '@/components/feature/GateValve3D';
import StoryScenes, { Scene02Copy, Scene03Copy } from './StoryScenes';
import SceneCallouts from './SceneCallouts';

gsap.registerPlugin(ScrollTrigger);

const SEQ_QUERY = '(min-width: 768px) and (prefers-reduced-motion: no-preference)';
const MARK_BASE = 400;


/**
 * Scenes 01–03 as ONE pinned viewport: one canvas, one GLB, one GSAP ScrollTrigger timeline (300vh travel).
 * Scroll progress (0..1) is the only animation state → fully reversible.
 * Mobile / reduced motion: static hero + static Scene 02/03 sections, no WebGL, no pinning.
 */
export default function Hero() {
  const stageRef = useRef<HTMLElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef(0);
  const [seq, setSeq] = useState(false);
  const [status, setStatus] = useState<GateValveStatus>('loading');
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(SEQ_QUERY);
    const update = () => setSeq(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const stage = stageRef.current;
    if (!seq || !stage) return undefined;
    const apply = (p: number) => {
      progressRef.current = p;
      stage.style.setProperty('--hp', p.toFixed(4));
      if (ctaRef.current) ctaRef.current.style.pointerEvents = p > 0.08 ? 'none' : 'auto';
    };
    const ctx = gsap.context(() => {
      const state = { p: 0 };
      gsap.timeline({
        scrollTrigger: {
          trigger: stage,
          start: 'top top',
          end: '+=300%',
          pin: true,
          scrub: 0.6,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      }).to(state, { p: 1, duration: 1, ease: 'none', onUpdate: () => apply(state.p) });
    }, stage);
    return () => {
      ctx.revert();
      apply(0);
    };
  }, [seq]);

  // Framing rects in canvas (= stage) px. S1 is measured from the plinth slot on the cover-cropped photo.
  const getFrames = useCallback((w: number, h: number): SceneFrames => {
    const stage = stageRef.current?.getBoundingClientRect();
    const a = anchorRef.current?.getBoundingClientRect();
    const r1 = stage && a
      ? { x: a.left - stage.left, y: a.top - stage.top, w: a.width, h: a.height }
      : slotRect(w * 0.5, h * 0.45, h * 0.5);
    return {
      r1,
      r2: slotRect(w * 0.74, h * 0.52, h * 0.6),
      // top-down: target (handwheel centre) sits at frame centre → vertically centred in the viewport
      r3: slotRect(w * 0.68, h * 0.5, h * 0.95),
    };
  }, []);

  const onHandwheel = useCallback((x: number, y: number, r: number) => {
    const el = markRef.current;
    if (!el) return;
    const sc = r / (MARK_BASE * OPEN_ROTOR_RIM);
    el.style.transform = `translate(${x - MARK_BASE / 2}px, ${y - MARK_BASE / 2}px) scale(${sc.toFixed(4)})`;
  }, []);

  const ready = seq && status === 'ready';

  return (
    <>
      <div id="top" className="relative w-full">
        <section ref={stageRef} className="relative w-full h-[100svh] min-h-[640px] overflow-hidden bg-background-50">
          {/* ── Scene 01 dark photographic layer (fades to white) ── */}
          <div
            className="absolute inset-0 bg-foreground-950"
            style={{ opacity: 'clamp(0, calc(1 - (var(--hp, 0) - 0.08) * 4.2), 1)' }}
          >
            <div
              className="absolute left-1/2 top-[54%] md:top-1/2 -translate-x-1/2 -translate-y-1/2 w-full md:w-[max(100vw,calc(max(100svh,640px)*1672/941))]"
              style={{ aspectRatio: '1672 / 941' }}
            >
              <img
                src={assets.heroStage}
                alt="Dark mineral stage with ball valve, butterfly valve and red light curves"
                title="NORTHLINE valve stage"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 z-[5] bg-gradient-to-b from-black/30 via-transparent to-black/40 pointer-events-none" />
              <h1
                className="absolute inset-x-0 bottom-[calc(100%+32px)] md:bottom-auto md:top-[16%] z-10 text-center font-heading font-normal leading-[0.95] md:leading-[0.9] tracking-[-0.02em] text-foreground-50 text-[17vw] md:text-[clamp(52px,8.6vw,158px)] select-none"
                style={{ opacity: 'clamp(0, calc(1 - (var(--hp, 0) - 0.05) * 9), 1)' }}
              >
                CONTROL{' '}
                <br className="md:hidden" />
                FLOW.
              </h1>
              {/* Plinth slot: calibrated against the photo (rock top face ≈ 73% of image height) */}
              <div ref={anchorRef} className="absolute left-1/2 -translate-x-1/2 bottom-[25.9%] h-[50%] aspect-[1214/1295] z-20 pointer-events-none">
                <div className="absolute inset-0" style={{ opacity: 'clamp(0, calc(1 - var(--hp, 0) * 12), 1)' }}>
                  <div className={`absolute left-1/2 -translate-x-1/2 bottom-[-1.2%] w-[56%] h-[4.5%] rounded-[50%] bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.62)_0%,rgba(0,0,0,0.26)_45%,rgba(0,0,0,0)_72%)] blur-[3px] transition-opacity duration-700 ${ready ? 'opacity-100' : 'opacity-0'}`} />
                  <div className={`absolute left-1/2 -translate-x-1/2 bottom-[-0.2%] w-[32%] h-[1.6%] rounded-[50%] bg-black/65 blur-[2px] transition-opacity duration-700 ${ready ? 'opacity-100' : 'opacity-0'}`} />
                </div>
                <img
                  src={assets.gateValve}
                  alt="NORTHLINE industrial gate valve with red handwheel"
                  title="Industrial gate valve - NORTHLINE Flow Systems"
                  onLoad={() => setImgLoaded(true)}
                  className={`valve-poster absolute inset-0 w-full h-full object-contain object-bottom transition-opacity duration-700 ${imgLoaded ? 'is-loaded' : ''} ${ready ? '!opacity-0' : ''}`}
                />
              </div>
            </div>
          </div>

          {/* ── One persistent full-viewport canvas ── */}
          {seq && (
            <GateValve3D
              progressRef={progressRef}
              getFrames={getFrames}
              onHandwheel={onHandwheel}
              onStatus={(s) => setStatus(s)}
              className={`absolute inset-0 z-20 pointer-events-none transition-opacity duration-700 ${ready ? 'opacity-100' : 'opacity-0'}`}
            />
          )}

          {seq && (
            <>
              {/* Scene 02 copy */}
              <div
                className="absolute inset-0 z-30 flex items-center pointer-events-none"
                style={{ opacity: 'clamp(0, min(calc((var(--hp, 0) - 0.26) * 10), calc((0.54 - var(--hp, 0)) * 12)), 1)' }}
              >
                <div className="w-full max-w-[1280px] mx-auto px-6 lg:px-12">
                  <div className="w-[40%] min-w-[340px]"><Scene02Copy /></div>
                </div>
              </div>
              {/* Scene 02 technical callouts */}
              <div
                className="absolute inset-0 z-30 pointer-events-none"
                style={{ opacity: 'clamp(0, min(calc((var(--hp, 0) - 0.32) * 10), calc((0.5 - var(--hp, 0)) * 12)), 1)' }}
              >
                <SceneCallouts />
              </div>
              {/* Scene 03 copy */}
              <div
                className="absolute inset-0 z-30 flex items-center pointer-events-none"
                style={{ opacity: 'clamp(0, calc((var(--hp, 0) - 0.62) * 10), 1)' }}
              >
                <div className="w-full max-w-[1280px] mx-auto px-6 lg:px-12">
                  <div className="w-[38%] min-w-[320px]"><Scene03Copy /></div>
                </div>
              </div>
              {/* Brand memory: SVG handwheel aligned to the projected real handwheel */}
              <div
                className="absolute inset-0 z-[25] pointer-events-none"
                style={{ opacity: 'clamp(0, calc((var(--hp, 0) - 0.8) * 7), 1)' }}
              >
                <div ref={markRef} className="absolute left-0 top-0 origin-center" style={{ width: MARK_BASE, height: MARK_BASE }}>
                  <div
                    className="w-full h-full origin-center"
                    style={{ transform: 'scale(clamp(0.92, calc(0.92 + (var(--hp, 0) - 0.8) * 0.8), 1))' }}
                  >
                    <OpenRotorMark size={MARK_BASE} title="NORTHLINE Open Rotor" />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Scene 01 copy + controls */}
          <div
            ref={ctaRef}
            className="absolute inset-x-0 bottom-0 z-30 w-full"
            style={{ opacity: 'clamp(0, calc(1 - var(--hp, 0) * 10), 1)' }}
          >
            <div className="px-6 pb-8 md:pb-10 flex justify-center">
              <p className="max-w-[380px] md:-translate-y-[50px] text-center font-sans text-[13px] md:text-sm font-light leading-[1.7] tracking-[0.01em] text-foreground-50/80">
                Industrial valves and flow-control components, selected for dependable performance and efficient procurement.
              </p>
            </div>
          </div>
        </section>
      </div>
      {!seq && <StoryScenes />}
    </>
  );
}